import { useState } from "react";
import { Divider, Typography } from "@mui/material";
import LoginHeader from "../Login/LoginHeader";
import { TextInput } from "../../components-redesign/TextInput/TextInput";
import PrimaryButton from "../../components-redesign/StyledButtons/PrimaryButton";
import PrimaryLink from "../../components-redesign/StyledLinks/PrimaryLink";
import PrivacyPolicy from "./PrivacyPolicy";
import ErrorAlert from "../../components-redesign/Error/ErrorAlert";
import { useLocalTranslationState } from "../../context/AppContext/AppContext";
import { styled } from "@mui/material/styles";
import { myVJBlack200 } from "../../constants/brandingColors";
import { validateEmail } from "../../utilities/validations";
import Utils from "../../utilities/utils";

const FormContainer = styled("form")(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  flexDirection: 'column',
  [theme.breakpoints.down('sm')]: {
    minWidth: '100%',
  },
}));

const LoginBtnContainer = styled("div")(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'flex-end',
  flexDirection: "column",
  paddingTop: theme.spacing(2.5),
}));

const EmptyDivContainer = styled("div")({
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
});

const TextContainer = styled("div")({
  alignSelf: "stretch",
});

const HeaderContainer = styled("div")(({ theme }) => ({
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(4.5),
}));

const CreateAccountLinkContainer = styled("div")(({ theme }) => ({
  display: "flex",
  alignSelf: "flex-start",
  paddingTop: theme.spacing(1),
}));

interface RegisterEmailProps {
  username;
  setUsername: (username) => void;
  setLoginState: (state) => void
  authError: string;
  setAuthError: (error) => void;
  setCreateAccountState: () => void;
}

const RegisterEmail = ({
  username,
  setUsername,
  setLoginState,
  setCreateAccountState,
  authError,
  setAuthError,
}: RegisterEmailProps) => {

  const { _translate } = useLocalTranslationState();

  const [showErrorAlert, setShowErrorAlert] = useState(false);

  const isValid = () => {
    if (username) {
      if (validateEmail(username)) {
        setAuthError("")
        return true
      } else {
        setAuthError(_translate("Error, please correct this field"))
        return false
      }
    } else {
      setAuthError(_translate("Error, please complete this field"))
      return false
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isValid()) {
      const SSOUrl = Utils.getSSOUrl(username);
      if (SSOUrl) {
        setShowErrorAlert(true);
        setAuthError(_translate("Error, an SSO account already exists"))
      } else {
        setCreateAccountState();
      }
    }
  };

  return (
    <>
      <FormContainer noValidate onSubmit={handleSubmit}>
        {showErrorAlert && (
          <ErrorAlert
            title={_translate("Account creation error")}
            subTitle={_translate("Your organization has already set up your account using SSO")}
            buttonText={_translate("Log in")}
            onClick={setLoginState}
          />
        )}
        <TextContainer>
          <Typography variant="subtitle2" fontWeight="400">
            {_translate("Step 1 of 4")}
          </Typography>
        </TextContainer>
        <HeaderContainer>
          <LoginHeader text={_translate("Register your email address")} />
        </HeaderContainer>
        <TextInput
          fieldName="email"
          id="email"
          dataTestId="email"
          label={_translate("Email address")}
          value={username}
          isDisabled={false}
          isRequired={true}
          autoFocus={true}
          errorInPlainText={false}
          error={authError}
          onClear={() => {
            setUsername('');
            setShowErrorAlert(false);
            setAuthError('');
          }}
          onChange={(event) => {
            const value = event.target.value;
            setUsername(() => value);
            if (value) {
              setAuthError('');
            }
          }}
        />

        <LoginBtnContainer>
          <PrimaryButton
            id="login-next-btn"
            text={_translate("Next")}
            dataTestId="next-btn"
            isDisabled={showErrorAlert}
          />
        </LoginBtnContainer>

        <EmptyDivContainer
          sx={(theme) => ({
            paddingBottom: theme.spacing(8),
            [theme.breakpoints.up('lg')]: {
              paddingBottom: theme.spacing(4),
            },
          })}
        />

        <Divider sx={{ borderColor: myVJBlack200 }} />
        <LoginBtnContainer>
          <TextContainer>
            <Typography variant="subtitle1" fontWeight="400">
              {_translate("Already have a Connect account")}
            </Typography>
          </TextContainer>
          <CreateAccountLinkContainer>
            <PrimaryLink
              text={_translate("Log in")}
              onClick={setLoginState} />
          </CreateAccountLinkContainer>
        </LoginBtnContainer>

        <PrivacyPolicy />

      </FormContainer >
    </>
  );
};

export default RegisterEmail;